import { useState } from 'react'
import {
  Download, Building2, Globe, AlertTriangle, BarChart3,
  Calendar, ShoppingBag, TrendingDown, Users, Clock
} from 'lucide-react'
import type { JobDetail, DeepAnalysis, VendorBreakdown, LostVATVendor, EntityDeep, DeadlineRow } from '../types'
import { fmtEUR, fmtNum, fmtPct, categoryLabel, categoryTagClass } from '../utils/format'
import { StatCard, CountryBar, ProgressSplit, InfoBox } from './UI'
import { exportUrl } from '../api'
import clsx from 'clsx'

const TABS = [
  { key: 'overview',   label: 'Overview',        icon: BarChart3 },
  { key: 'vendors',    label: 'Vendors',          icon: ShoppingBag },
  { key: 'entities',   label: 'Entities',         icon: Users },
  { key: 'countries',  label: 'By Country',       icon: Globe },
  { key: 'trend',      label: 'Monthly Trend',    icon: Calendar },
  { key: 'lost',       label: 'Lost VAT',         icon: TrendingDown },
  { key: 'ineligible', label: 'Ineligible',       icon: AlertTriangle },
  { key: 'deadlines',  label: 'Deadlines',        icon: Clock },
]

const STATUS_COLORS: Record<string, string> = {
  domestic:      '#0096c7',
  foreign_noneu: '#ea580c',
  foreign_eu:    '#7c3aed',
}

/* ── Sparkline bar ── */
function MiniBar({ value, max, color = '#0096c7' }: { value: number; max: number; color?: string }) {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0
  return (
    <div style={{ height: 6, background: '#f1f5f9', borderRadius: 99, overflow: 'hidden', width: '100%' }}>
      <div style={{ height: '100%', width: pct + '%', background: color, borderRadius: 99, transition: 'width .5s' }} />
    </div>
  )
}

/* ── Monthly chart ── */
function MonthlyChart({ data }: { data: DeepAnalysis['monthly_trend'] }) {
  const maxVat = Math.max(...data.map(d => d.vat), 1)
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end', height: 120, padding: '0 4px' }}>
      {data.map(d => {
        const h = Math.max((d.vat / maxVat) * 100, 2)
        const [, mm] = d.month.split('-')
        const months = ['','Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
        return (
          <div key={d.month} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
            <div style={{ fontSize: 9, color: '#94a3b8', fontFamily: 'JetBrains Mono', whiteSpace: 'nowrap' }}>
              {fmtEUR(d.vat, 0)}
            </div>
            <div
              title={`${d.month}: ${fmtEUR(d.vat)} VAT · ${fmtNum(d.txns)} txns`}
              style={{
                width: '100%', height: h + '%', background: '#0096c7',
                borderRadius: '4px 4px 0 0', minHeight: 4,
                transition: 'height .5s', cursor: 'default',
                opacity: 0.8,
              }}
            />
            <div style={{ fontSize: 9, color: '#94a3b8', whiteSpace: 'nowrap' }}>{months[parseInt(mm)]}</div>
          </div>
        )
      })}
    </div>
  )
}

/* ── Vendor tag list ── */
function TagList({ items, color = '#0096c7' }: { items: string[]; color?: string }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
      {items.slice(0, 4).map(item => (
        <span key={item} style={{
          fontSize: 11, padding: '1px 7px', borderRadius: 4,
          background: color + '15', color: color,
          border: `1px solid ${color}30`, fontWeight: 500,
        }}>{item}</span>
      ))}
      {items.length > 4 && (
        <span style={{ fontSize: 11, color: '#94a3b8' }}>+{items.length - 4} more</span>
      )}
    </div>
  )
}

/* ── Main component ── */
export default function JobResultPanel({ job }: { job: JobDetail }) {
  const [tab, setTab] = useState('overview')
  const r = job.result
  if (!r) return null
  const s   = r.summary
  const deep = r.deep
  const topCountryVAT = r.country_breakdown[0]?.vat ?? 1

  return (
    <div className="card">
      {/* Header */}
      <div className="card-header">
        <div>
          <p style={{ fontWeight: 600, fontSize: 15, color: '#0f172a', marginBottom: 3 }}>
            {job.original_filename}
          </p>
          <p style={{ fontSize: 12, color: '#475569' }}>
            {fmtNum(s.total_transactions)} transactions &nbsp;·&nbsp; {fmtNum(s.eligible_transactions)} eligible &nbsp;·&nbsp;
            1 EUR = {(1 / s.inr_eur_rate).toFixed(2)} INR
          </p>
        </div>
        <a href={exportUrl(job.id)} download className="btn btn-success">
          <Download size={13} /> Download Excel
        </a>
      </div>

      <div className="card-body">
        {/* KPI row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(158px,1fr))', gap: 14, marginBottom: 26 }}>
          <StatCard label="Total VAT Potential" value={fmtEUR(s.total_vat_eur)}
            sub={`${fmtNum(s.eligible_transactions)} eligible`} accent="#0096c7" />
          <StatCard label="Domestic VAT" value={fmtEUR(s.domestic_vat_eur)} sub="SG · JP · AU · NZ" accent="#0284c7" />
          <StatCard label="Foreign Non-EU" value={fmtEUR(s.foreign_noneu_vat_eur)} sub="NO · FI · SE · GB · NL" accent="#ea580c" />
          <StatCard label="Eligible Spend" value={fmtEUR(s.eligible_spend_eur, 0)}
            sub={`${fmtPct((s.eligible_spend_eur / s.total_spend_eur) * 100)} of total`} accent="#22c55e" />
          <StatCard label="Unique Vendors" value={deep ? fmtNum(deep.vendor_breakdown.length) : '—'}
            sub="in eligible transactions" accent="#7c3aed" />
          <StatCard label="Total Spend" value={fmtEUR(s.total_spend_eur, 0)}
            sub={`${fmtNum(s.total_transactions)} transactions`} accent="#94a3b8" />
        </div>

        {/* Tabs */}
        <div className="tab-list">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button key={key} onClick={() => setTab(key)}
              className={clsx('tab-item', tab === key && 'active')}>
              <Icon size={13} />{label}
            </button>
          ))}
        </div>

        {/* ── OVERVIEW ── */}
        {tab === 'overview' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 28 }}>
            <div>
              <p style={{ fontSize: 11.5, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '.6px', marginBottom: 14 }}>
                Top Countries by VAT Potential
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {r.country_breakdown.slice(0, 10).map(c => (
                  <CountryBar key={c.iso} country={c.country} value={c.vat}
                    max={topCountryVAT} formatted={fmtEUR(c.vat, 0)} category={c.status} />
                ))}
              </div>
            </div>
            <div>
              <p style={{ fontSize: 11.5, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '.6px', marginBottom: 14 }}>
                VAT Split
              </p>
              <ProgressSplit total={s.total_vat_eur} segments={[
                { label: 'Domestic (SG/JP/AU/NZ)',   value: s.domestic_vat_eur,      color: '#0096c7' },
                { label: 'Foreign Non-EU',            value: s.foreign_noneu_vat_eur, color: '#ea580c' },
                { label: 'Foreign EU',                value: s.foreign_eu_vat_eur,    color: '#7c3aed' },
              ]} />

              {/* Summary stats box */}
              <div style={{ marginTop: 20, background: '#f8fafc', borderRadius: 10, padding: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 20px' }}>
                {[
                  ['Recovery Rate',     fmtPct((s.total_vat_eur / s.total_spend_eur) * 100, 2), '#15803d'],
                  ['Ineligible Spend',  fmtEUR(s.total_spend_eur - s.eligible_spend_eur, 0),    '#b91c1c'],
                  ['Eligible Txns',     fmtNum(s.eligible_transactions),                         '#1d4ed8'],
                  ['Ineligible Txns',   fmtNum(s.ineligible_transactions),                       '#92400e'],
                ].map(([label, val, color]) => (
                  <div key={String(label)}>
                    <p style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: 4 }}>{label}</p>
                    <p className="mono" style={{ fontSize: 15, fontWeight: 700, color: String(color) }}>{val}</p>
                  </div>
                ))}
              </div>

              {/* Monthly mini preview */}
              {deep && deep.monthly_trend.length > 0 && (
                <div style={{ marginTop: 20, background: '#f8fafc', borderRadius: 10, padding: '14px 16px' }}>
                  <p style={{ fontSize: 11.5, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '.6px', marginBottom: 12 }}>
                    Monthly VAT Potential
                  </p>
                  <MonthlyChart data={deep.monthly_trend} />
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── VENDORS ── */}
        {tab === 'vendors' && deep && (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 22 }}>
              <div style={{ background: '#f8fafc', borderRadius: 10, padding: '14px 16px' }}>
                <p style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '.6px', marginBottom: 6 }}>Unique Vendors</p>
                <p className="mono" style={{ fontSize: 22, fontWeight: 700, color: '#0f172a' }}>{fmtNum(deep.vendor_breakdown.length)}</p>
              </div>
              <div style={{ background: '#f8fafc', borderRadius: 10, padding: '14px 16px' }}>
                <p style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '.6px', marginBottom: 6 }}>Top Vendor VAT</p>
                <p className="mono" style={{ fontSize: 22, fontWeight: 700, color: '#15803d' }}>{fmtEUR(deep.vendor_breakdown[0]?.vat ?? 0)}</p>
                <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 3 }}>{deep.vendor_breakdown[0]?.vendor?.slice(0, 28)}</p>
              </div>
              <div style={{ background: '#f8fafc', borderRadius: 10, padding: '14px 16px' }}>
                <p style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '.6px', marginBottom: 6 }}>Top 5 Vendors Share</p>
                <p className="mono" style={{ fontSize: 22, fontWeight: 700, color: '#0096c7' }}>
                  {fmtPct(deep.vendor_breakdown.slice(0,5).reduce((s,v)=>s+v.vat,0) / s.total_vat_eur * 100)}
                </p>
              </div>
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table className="data-table">
                <thead><tr>
                  <th>#</th>
                  <th>Vendor</th>
                  <th style={{ textAlign: 'right' }}>Transactions</th>
                  <th style={{ textAlign: 'right' }}>Spend (EUR)</th>
                  <th style={{ textAlign: 'right' }}>VAT Potential</th>
                  <th>VAT Share</th>
                  <th>Countries</th>
                  <th>Entities</th>
                </tr></thead>
                <tbody>
                  {deep.vendor_breakdown.map((v: VendorBreakdown, i: number) => {
                    const maxVat = deep.vendor_breakdown[0]?.vat ?? 1
                    return (
                      <tr key={v.vendor}>
                        <td style={{ fontSize: 11.5, color: '#94a3b8', width: 32 }}>{i + 1}</td>
                        <td style={{ fontWeight: 500, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {v.vendor}
                        </td>
                        <td className="mono" style={{ textAlign: 'right' }}>{fmtNum(v.txns)}</td>
                        <td className="mono" style={{ textAlign: 'right' }}>{fmtEUR(v.spend, 0)}</td>
                        <td className="mono" style={{ textAlign: 'right', fontWeight: 700, color: '#15803d' }}>{fmtEUR(v.vat)}</td>
                        <td style={{ minWidth: 100 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <MiniBar value={v.vat} max={maxVat} color="#0096c7" />
                            <span className="mono" style={{ fontSize: 11, color: '#475569', width: 36, textAlign: 'right', flexShrink: 0 }}>
                              {fmtPct(v.vat / s.total_vat_eur * 100, 1)}
                            </span>
                          </div>
                        </td>
                        <td><TagList items={v.countries} color="#0e7490" /></td>
                        <td><TagList items={v.entities} color="#6d28d9" /></td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── ENTITIES ── */}
        {tab === 'entities' && deep && (
          <div>
            {/* Entity cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))', gap: 16, marginBottom: 28 }}>
              {deep.entity_deep.map((e: EntityDeep) => (
                <div key={e.entity} style={{ background: '#f8fafc', borderRadius: 12, padding: 18, border: '1px solid #e2e8f0' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
                    <div>
                      <p style={{ fontWeight: 700, fontSize: 16, color: '#0f172a' }}>{e.entity}</p>
                      <p style={{ fontSize: 12, color: '#475569', marginTop: 2 }}>
                        {fmtNum(e.txns)} txns · {e.vendor_cnt} vendors · {e.country_cnt} countries
                      </p>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <p className="mono" style={{ fontSize: 18, fontWeight: 700, color: '#15803d' }}>{fmtEUR(e.vat)}</p>
                      <p style={{ fontSize: 11, color: '#94a3b8' }}>VAT potential</p>
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
                    {[
                      ['Spend', fmtEUR(e.spend, 0), '#475569'],
                      ['Domestic VAT', fmtEUR(e.domestic_vat), '#0096c7'],
                      ['Foreign VAT', fmtEUR(e.foreign_vat), '#ea580c'],
                      ['Top Country', e.top_country || '—', '#7c3aed'],
                    ].map(([lbl, val, color]) => (
                      <div key={String(lbl)} style={{ background: '#ffffff', borderRadius: 8, padding: '8px 10px', border: '1px solid #e2e8f0' }}>
                        <p style={{ fontSize: 10.5, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 3 }}>{lbl}</p>
                        <p className="mono" style={{ fontSize: 13, fontWeight: 600, color: String(color) }}>{val}</p>
                      </div>
                    ))}
                  </div>
                  {/* VAT split mini bar */}
                  {e.vat > 0 && (
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>
                        <span>Domestic</span><span>Foreign</span>
                      </div>
                      <div style={{ height: 6, background: '#e2e8f0', borderRadius: 99, overflow: 'hidden', display: 'flex' }}>
                        <div style={{ width: `${(e.domestic_vat / e.vat) * 100}%`, background: '#0096c7', transition: 'width .5s' }} />
                        <div style={{ flex: 1, background: '#ea580c' }} />
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Entity × Country matrix */}
            <p style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', marginBottom: 14 }}>
              Entity × Country Breakdown
            </p>
            {Object.entries(deep.entity_country_matrix).map(([entity, rows]) => (
              <div key={entity} style={{ marginBottom: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                  <span style={{ fontWeight: 700, fontSize: 13, color: '#0f172a', background: '#e0f2fe', padding: '2px 10px', borderRadius: 6, border: '1px solid #bae6fd' }}>
                    {entity}
                  </span>
                  <span style={{ fontSize: 12, color: '#475569' }}>
                    {fmtEUR(rows.reduce((s: number, r: any) => s + r.vat, 0))} total VAT
                  </span>
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table className="data-table" style={{ fontSize: 13 }}>
                    <thead><tr>
                      <th>Country</th>
                      <th style={{ textAlign: 'right' }}>Transactions</th>
                      <th style={{ textAlign: 'right' }}>Spend (EUR)</th>
                      <th style={{ textAlign: 'right' }}>VAT</th>
                      <th>Type</th>
                      <th>Share</th>
                    </tr></thead>
                    <tbody>
                      {(rows as any[]).map((row: any) => {
                        const entityTotal = (rows as any[]).reduce((s: number, r: any) => s + r.vat, 0)
                        return (
                          <tr key={row.country}>
                            <td style={{ fontWeight: 500 }}>{row.country}</td>
                            <td className="mono" style={{ textAlign: 'right' }}>{fmtNum(row.txns)}</td>
                            <td className="mono" style={{ textAlign: 'right' }}>{fmtEUR(row.spend, 0)}</td>
                            <td className="mono" style={{ textAlign: 'right', fontWeight: 700, color: '#15803d' }}>{fmtEUR(row.vat)}</td>
                            <td><span className={`tag tag-${row.status === 'domestic' ? 'domestic' : row.status === 'foreign_noneu' ? 'foreign-noneu' : 'foreign-eu'}`}>
                              {row.status === 'domestic' ? 'Domestic' : row.status === 'foreign_noneu' ? 'Non-EU' : 'EU'}
                            </span></td>
                            <td style={{ minWidth: 80 }}>
                              <MiniBar value={row.vat} max={entityTotal} color={STATUS_COLORS[row.status] ?? '#0096c7'} />
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── COUNTRIES ── */}
        {tab === 'countries' && (
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table">
              <thead><tr>
                <th>Country</th><th>ISO</th>
                <th style={{ textAlign: 'right' }}>Hotel Rate</th>
                <th style={{ textAlign: 'right' }}>Transactions</th>
                <th style={{ textAlign: 'right' }}>Spend (EUR)</th>
                <th style={{ textAlign: 'right' }}>VAT Potential</th>
                <th>Share</th>
                <th>Category</th>
              </tr></thead>
              <tbody>
                {r.country_breakdown.map((c, i) => (
                  <tr key={c.iso}>
                    <td style={{ fontWeight: 500 }}>{c.country}</td>
                    <td className="mono" style={{ fontSize: 11.5, color: '#94a3b8' }}>{c.iso}</td>
                    <td className="mono" style={{ textAlign: 'right' }}>{c.vat_rate}%</td>
                    <td className="mono" style={{ textAlign: 'right' }}>{fmtNum(c.txns)}</td>
                    <td className="mono" style={{ textAlign: 'right' }}>{fmtEUR(c.spend)}</td>
                    <td className="mono" style={{ textAlign: 'right', fontWeight: 700, color: '#15803d' }}>{fmtEUR(c.vat)}</td>
                    <td style={{ minWidth: 100 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <MiniBar value={c.vat} max={topCountryVAT} color={STATUS_COLORS[c.status] ?? '#0096c7'} />
                        <span className="mono" style={{ fontSize: 11, color: '#475569', width: 36, textAlign: 'right', flexShrink: 0 }}>
                          {fmtPct(c.vat / s.total_vat_eur * 100, 1)}
                        </span>
                      </div>
                    </td>
                    <td><span className={`tag ${categoryTagClass(c.status)}`}>{categoryLabel(c.status)}</span></td>
                  </tr>
                ))}
              </tbody>
              <tfoot><tr>
                <td colSpan={4} style={{ fontWeight: 700 }}>Grand Total</td>
                <td className="mono" style={{ textAlign: 'right' }}>{fmtEUR(s.eligible_spend_eur)}</td>
                <td className="mono" style={{ textAlign: 'right', color: '#15803d' }}>{fmtEUR(s.total_vat_eur)}</td>
                <td colSpan={2} />
              </tr></tfoot>
            </table>
          </div>
        )}

        {/* ── MONTHLY TREND ── */}
        {tab === 'trend' && deep && (
          <div>
            <div style={{ background: '#f8fafc', borderRadius: 12, padding: '20px 22px', marginBottom: 24, border: '1px solid #e2e8f0' }}>
              <p style={{ fontSize: 12, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '.6px', marginBottom: 16 }}>
                Monthly VAT Potential (EUR)
              </p>
              <MonthlyChart data={deep.monthly_trend} />
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table className="data-table">
                <thead><tr>
                  <th>Month</th>
                  <th style={{ textAlign: 'right' }}>Transactions</th>
                  <th style={{ textAlign: 'right' }}>Eligible Spend</th>
                  <th style={{ textAlign: 'right' }}>VAT Potential</th>
                  <th>% of Total</th>
                  <th>Trend</th>
                </tr></thead>
                <tbody>
                  {deep.monthly_trend.map((m, i) => {
                    const maxVat = Math.max(...deep.monthly_trend.map(d => d.vat), 1)
                    const prev   = deep.monthly_trend[i - 1]
                    const change = prev && prev.vat > 0 ? ((m.vat - prev.vat) / prev.vat * 100) : null
                    return (
                      <tr key={m.month}>
                        <td style={{ fontWeight: 500 }}>{m.month}</td>
                        <td className="mono" style={{ textAlign: 'right' }}>{fmtNum(m.txns)}</td>
                        <td className="mono" style={{ textAlign: 'right' }}>{fmtEUR(m.spend, 0)}</td>
                        <td className="mono" style={{ textAlign: 'right', fontWeight: 700, color: '#15803d' }}>{fmtEUR(m.vat)}</td>
                        <td className="mono" style={{ fontSize: 12, color: '#475569' }}>
                          {fmtPct(m.vat / s.total_vat_eur * 100)}
                        </td>
                        <td style={{ minWidth: 140, display: 'flex', alignItems: 'center', gap: 8 }}>
                          <MiniBar value={m.vat} max={maxVat} color="#0096c7" />
                          {change !== null && (
                            <span className="mono" style={{ fontSize: 11, color: change >= 0 ? '#15803d' : '#b91c1c', flexShrink: 0 }}>
                              {change >= 0 ? '↑' : '↓'}{Math.abs(change).toFixed(0)}%
                            </span>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── LOST VAT ── */}
        {tab === 'lost' && deep && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <InfoBox variant="amber">
                <strong>Lost VAT</strong> — these vendors had VAT applicable but transactions were ineligible
                (no reciprocity, deadline passed, or ineligible expense type). Total potential lost:{' '}
                <strong>{fmtEUR(deep.lost_vat_vendors.reduce((s, v) => s + v.potential_vat, 0))}</strong>
              </InfoBox>
            </div>

            {deep.lost_vat_vendors.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 0', color: '#94a3b8' }}>
                No lost VAT identified — all VAT-applicable transactions are eligible.
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className="data-table">
                  <thead><tr>
                    <th>#</th>
                    <th>Vendor</th>
                    <th style={{ textAlign: 'right' }}>Transactions</th>
                    <th style={{ textAlign: 'right' }}>Spend (EUR)</th>
                    <th style={{ textAlign: 'right' }}>Potential VAT Lost</th>
                    <th>Reason</th>
                    <th>Countries</th>
                  </tr></thead>
                  <tbody>
                    {deep.lost_vat_vendors.map((v: LostVATVendor, i: number) => (
                      <tr key={v.vendor}>
                        <td style={{ fontSize: 11.5, color: '#94a3b8', width: 32 }}>{i + 1}</td>
                        <td style={{ fontWeight: 500, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.vendor}</td>
                        <td className="mono" style={{ textAlign: 'right' }}>{fmtNum(v.txns)}</td>
                        <td className="mono" style={{ textAlign: 'right' }}>{fmtEUR(v.spend, 0)}</td>
                        <td className="mono" style={{ textAlign: 'right', fontWeight: 700, color: '#b91c1c' }}>{fmtEUR(v.potential_vat)}</td>
                        <td>
                          <span style={{ fontSize: 11.5, padding: '2px 8px', borderRadius: 6, background: '#fff7ed', color: '#c2410c', border: '1px solid #fed7aa', fontWeight: 600 }}>
                            {v.reason.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </span>
                        </td>
                        <td><TagList items={v.countries} color="#0e7490" /></td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot><tr>
                    <td colSpan={4} style={{ fontWeight: 700 }}>Total Lost</td>
                    <td className="mono" style={{ textAlign: 'right', color: '#b91c1c', fontWeight: 700 }}>
                      {fmtEUR(deep.lost_vat_vendors.reduce((s, v) => s + v.potential_vat, 0))}
                    </td>
                    <td colSpan={2} />
                  </tr></tfoot>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ── INELIGIBLE ── */}
        {tab === 'ineligible' && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <InfoBox variant="amber">
                {r.ineligible_breakdown.map(b => `${b.reason}: ${fmtNum(b.txns)} txns (${fmtEUR(b.spend, 0)})`).join(' · ')}
              </InfoBox>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table className="data-table">
                <thead><tr>
                  <th>Reason</th>
                  <th style={{ textAlign: 'right' }}>Transactions</th>
                  <th style={{ textAlign: 'right' }}>Spend (EUR)</th>
                  <th>% of Total Spend</th>
                  <th>Notes</th>
                </tr></thead>
                <tbody>
                  {r.ineligible_breakdown.map(b => (
                    <tr key={b.status}>
                      <td style={{ fontWeight: 600, color: '#b91c1c' }}>{b.reason}</td>
                      <td className="mono" style={{ textAlign: 'right' }}>{fmtNum(b.txns)}</td>
                      <td className="mono" style={{ textAlign: 'right' }}>{fmtEUR(b.spend, 0)}</td>
                      <td style={{ minWidth: 140 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <MiniBar value={b.spend} max={s.total_spend_eur} color="#ef4444" />
                          <span className="mono" style={{ fontSize: 11, color: '#475569', width: 36, flexShrink: 0 }}>
                            {fmtPct(b.spend / s.total_spend_eur * 100, 1)}
                          </span>
                        </div>
                      </td>
                      <td style={{ fontSize: 12, color: '#475569', maxWidth: 280 }}>{b.notes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      {/* ── DEADLINES ── */}
      {tab === 'deadlines' && deep && deep.deadline_summary && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14, marginBottom: 22 }}>
            {[
              ['Overdue',  deep.deadline_summary.filter((d: DeadlineRow) => d.deadline_status === 'overdue').length,  '#b91c1c', '#fef2f2'],
              ['Urgent (≤30 days)', deep.deadline_summary.filter((d: DeadlineRow) => d.deadline_status === 'urgent').length,   '#92400e', '#fffbeb'],
              ['Open',     deep.deadline_summary.filter((d: DeadlineRow) => d.deadline_status === 'open').length,    '#15803d', '#f0fdf4'],
            ].map(([label, count, color, bg]) => (
              <div key={String(label)} style={{ background: String(bg), border: `1px solid ${String(color)}30`, borderRadius: 12, padding: '16px 18px' }}>
                <p style={{ fontSize: 11, fontWeight: 600, color: String(color), textTransform: 'uppercase', letterSpacing: '.6px', marginBottom: 6 }}>{label}</p>
                <p className="mono" style={{ fontSize: 28, fontWeight: 700, color: String(color) }}>{count}</p>
                <p style={{ fontSize: 12, color: String(color), opacity: 0.7, marginTop: 3 }}>claim periods</p>
              </div>
            ))}
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table className="data-table">
              <thead><tr>
                <th>Country</th>
                <th>Claim Period</th>
                <th>Country Deadline</th>
                <th>Internal Deadline</th>
                <th style={{ textAlign: 'right' }}>Days Remaining</th>
                <th>Status</th>
                <th style={{ textAlign: 'right' }}>Transactions</th>
                <th style={{ textAlign: 'right' }}>VAT Potential</th>
              </tr></thead>
              <tbody>
                {deep.deadline_summary.map((d: DeadlineRow) => {
                  const rowBg = d.deadline_status === 'overdue' ? '#fef2f2' : d.deadline_status === 'urgent' ? '#fffbeb' : '#ffffff'
                  const textColor = d.deadline_status === 'overdue' ? '#b91c1c' : d.deadline_status === 'urgent' ? '#92400e' : '#0f172a'
                  const statusLabel = d.deadline_status === 'overdue' ? '✗ Overdue' : d.deadline_status === 'urgent' ? '⚠ Urgent' : '✓ Open'
                  return (
                    <tr key={`${d.iso}-${d.claim_period}`}>
                      <td style={{ fontWeight: 600, background: rowBg }}>{d.country}</td>
                      <td className="mono" style={{ fontSize: 12, background: rowBg }}>{d.claim_period}</td>
                      <td className="mono" style={{ fontSize: 12, background: rowBg }}>{d.country_deadline}</td>
                      <td className="mono" style={{ fontSize: 12, fontWeight: d.deadline_status !== 'open' ? 700 : 400, color: textColor, background: rowBg }}>{d.internal_deadline}</td>
                      <td className="mono" style={{ textAlign: 'right', fontWeight: 700, color: textColor, background: rowBg }}>
                        {d.days_to_internal !== null ? d.days_to_internal : '—'}
                      </td>
                      <td style={{ background: rowBg }}>
                        <span style={{
                          fontSize: 12, fontWeight: 600, padding: '2px 10px', borderRadius: 6,
                          background: d.deadline_status === 'overdue' ? '#fecaca' : d.deadline_status === 'urgent' ? '#fde68a' : '#bbf7d0',
                          color: textColor,
                        }}>{statusLabel}</span>
                      </td>
                      <td className="mono" style={{ textAlign: 'right', background: rowBg }}>{fmtNum(d.txns)}</td>
                      <td className="mono" style={{ textAlign: 'right', fontWeight: 700, color: '#15803d', background: rowBg }}>{fmtEUR(d.vat)}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          <div style={{ marginTop: 16 }}>
            <InfoBox variant="blue">
              <strong>Internal deadline = 15 Nov</strong> (before country submission deadline).
              Urgent = within 30 days. Overdue = internal deadline has passed.
              Export to Excel for the full Deadlines Summary sheet with one row per country × period.
            </InfoBox>
          </div>
        </div>
      )}

      </div>
    </div>
  )
}
